import { Button, HStack, NativeSelect, Text, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Field } from '@mangovoice/mango-ui'

import { useDevicesI18n } from '../i18n'
import type {
  DeviceMassEditItem,
  ProvisioningDeviceListItem,
} from '../types/device.types'
import { getTransportHostValidationError } from '../utils/helpers'
import { SideSheetDialog } from './SideSheetDialog'

const TIMEZONE_VALUES = [
  'Alaska',
  'Arizona',
  'Atlantic Time (Canada)',
  'Central Time (US & Canada)',
  'Eastern Time (US & Canada)',
  'Greenwich Mean Time (UK)',
  'Hawaii',
  'Indiana (East)',
  'Mountain Time (US & Canada)',
  'Pacific Time (US & Canada)',
  'America/Vancouver',
  'Puerto Rico',
]

const TIMEZONE_LABELS: Record<string, string> = {
  'America/Vancouver': 'Pacific Time - British Columbia (PCT)',
}

interface MassEditFormState {
  transport: string
  nat_method: string
  jitter: string
  time_zone: string
  dst_enable: string
  allow_provision_retrieval: string
  server_group_host: string
}

const emptyFormState: MassEditFormState = {
  transport: '',
  nat_method: '',
  jitter: '',
  time_zone: '',
  dst_enable: '',
  allow_provision_retrieval: '',
  server_group_host: '',
}

interface DeviceMassEditDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedDevices: ProvisioningDeviceListItem[]
  onConfirm: (payload: DeviceMassEditItem[], reboot: boolean) => void
  isSubmitting: boolean
}

/**
 * Build mass-edit payload: only include fields that have a value.
 */
function buildPayload(
  deviceIds: number[],
  form: MassEditFormState,
): DeviceMassEditItem[] {
  const transport = form.transport.trim() || undefined
  const nat_method =
    form.nat_method === ''
      ? undefined
      : form.nat_method === 'stun'
        ? true
        : false
  const jitter = form.jitter.trim() || undefined
  const time_zone = form.time_zone.trim() || undefined
  const dst_enable =
    form.dst_enable === '' ? undefined : form.dst_enable === 'true'
  const allow_provision_retrieval =
    form.allow_provision_retrieval === ''
      ? undefined
      : form.allow_provision_retrieval === 'true'
  const server_group_host = form.server_group_host.trim() || undefined

  const baseItem: Omit<DeviceMassEditItem, 'id'> = {}
  if (transport !== undefined) baseItem.transport = transport
  if (nat_method !== undefined) baseItem.nat_method = nat_method
  if (jitter !== undefined) baseItem.jitter = jitter
  if (time_zone !== undefined) baseItem.time_zone = time_zone
  if (dst_enable !== undefined) baseItem.dst_enable = dst_enable
  if (allow_provision_retrieval !== undefined)
    baseItem.allow_provision_retrieval = allow_provision_retrieval
  if (server_group_host !== undefined)
    baseItem.server_group_host = server_group_host

  if (Object.keys(baseItem).length === 0) {
    return []
  }

  return deviceIds.map((id) => ({ id, ...baseItem }))
}

export const DeviceMassEditDialog = React.memo<DeviceMassEditDialogProps>(
  ({ isOpen, onClose, selectedDevices, onConfirm, isSubmitting }) => {
    const { text } = useDevicesI18n()
    const [formState, setFormState] =
      useState<MassEditFormState>(emptyFormState)

    const transportOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        { value: 'UDP', label: text('devices.options.transportUdp', 'UDP') },
        { value: 'TCP', label: text('devices.options.transportTcp', 'TCP') },
        { value: 'TLS', label: text('devices.options.transportTls', 'TLS') },
      ],
      [text],
    )

    const jitterOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        {
          value: 'normal',
          label: text('devices.options.jitterNormal', 'Normal'),
        },
        { value: 'low', label: text('devices.options.jitterLow', 'Low') },
        { value: 'high', label: text('devices.options.jitterHigh', 'High') },
      ],
      [text],
    )

    const timezoneOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        ...TIMEZONE_VALUES.map((tz) => ({
          value: tz,
          label: TIMEZONE_LABELS[tz] ?? tz,
        })),
      ],
      [text],
    )

    const booleanOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        { value: 'true', label: text('devices.common.yes', 'Yes') },
        { value: 'false', label: text('devices.common.no', 'No') },
      ],
      [text],
    )

    const natTraversalOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        { value: 'normal', label: text('devices.options.natNormal', 'Normal') },
        { value: 'stun', label: text('devices.options.natStun', 'STUN') },
      ],
      [text],
    )

    const serverGroupHostOptions = useMemo(
      () => [
        { value: '', label: text('devices.common.unchanged', 'Unchanged') },
        {
          value: 'primary',
          label: text('devices.options.serverGroupPrimary', 'Primary'),
        },
        {
          value: 'secondary',
          label: text('devices.options.serverGroupSecondary', 'Secondary'),
        },
      ],
      [text],
    )
    const [transportHostError, setTransportHostError] = useState<string | null>(
      null,
    )

    const resetForm = useCallback(() => {
      setFormState(emptyFormState)
      setTransportHostError(null)
    }, [])

    useEffect(() => {
      if (isOpen) {
        resetForm()
      }
    }, [isOpen, resetForm])

    const updateField = useCallback(
      <K extends keyof MassEditFormState>(
        field: K,
        value: MassEditFormState[K],
      ) => {
        setFormState((prev) => ({ ...prev, [field]: value }))
        if (field === 'transport' || field === 'server_group_host') {
          setTransportHostError(null)
        }
      },
      [],
    )

    const runSubmit = useCallback(
      (reboot: boolean) => {
        const validationError = getTransportHostValidationError(
          formState.transport,
          formState.server_group_host,
        )
        if (validationError) {
          setTransportHostError(validationError)
          return
        }
        const deviceIds = selectedDevices.map((d) => d.id)
        const payload = buildPayload(deviceIds, formState)
        if (payload.length === 0) {
          return
        }
        onConfirm(payload, reboot)
      },
      [selectedDevices, formState, onConfirm],
    )

    const payload = buildPayload(
      selectedDevices.map((d) => d.id),
      formState,
    )
    const currentTransportHostError = getTransportHostValidationError(
      formState.transport,
      formState.server_group_host,
    )
    const hasTransportHostError = Boolean(currentTransportHostError)
    const hasChanges = payload.length > 0

    return (
      <SideSheetDialog
        isOpen={isOpen}
        onClose={onClose}
        title={text('devices.massEdit.title', 'Mass Edit Devices')}
        width="480px"
        body={
          <>
            <Text mb={4} color="fg.muted" fontSize="sm">
              {text(
                'devices.massEdit.description',
                'Updating {{count}} device. Leave a field unchanged to keep current value.',
                {
                  count: selectedDevices.length,
                },
              )}
            </Text>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={hasTransportHostError}>
                <Field.Label>
                  {text('devices.massEdit.transport', 'Transport')}
                </Field.Label>
                <NativeSelect.Root size="sm" invalid={hasTransportHostError}>
                  <NativeSelect.Field
                    value={formState.transport}
                    onChange={(e) => updateField('transport', e.target.value)}
                  >
                    {transportOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text('devices.massEdit.natTraversal', 'NAT Traversal')}
                </Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={formState.nat_method}
                    onChange={(e) => updateField('nat_method', e.target.value)}
                  >
                    {natTraversalOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text('devices.massEdit.jitter', 'Jitter')}
                </Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={formState.jitter}
                    onChange={(e) => updateField('jitter', e.target.value)}
                  >
                    {jitterOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text('devices.massEdit.timeZone', 'Time Zone')}
                </Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={formState.time_zone}
                    onChange={(e) => updateField('time_zone', e.target.value)}
                  >
                    {timezoneOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text('devices.massEdit.daylightSaving', 'Daylight Saving')}
                </Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={formState.dst_enable}
                    onChange={(e) => updateField('dst_enable', e.target.value)}
                  >
                    {booleanOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label>
                  {text(
                    'devices.massEdit.allowProvisioning',
                    'Allow Provisioning',
                  )}
                </Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={formState.allow_provision_retrieval}
                    onChange={(e) =>
                      updateField('allow_provision_retrieval', e.target.value)
                    }
                  >
                    {booleanOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root invalid={hasTransportHostError}>
                <Field.Label>
                  {text(
                    'devices.massEdit.serverGroupHost',
                    'Server Group Host',
                  )}
                </Field.Label>
                <NativeSelect.Root size="sm" invalid={hasTransportHostError}>
                  <NativeSelect.Field
                    value={formState.server_group_host}
                    onChange={(e) =>
                      updateField('server_group_host', e.target.value)
                    }
                  >
                    {serverGroupHostOptions.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                {(transportHostError || currentTransportHostError) && (
                  <Field.ErrorText>
                    {transportHostError || currentTransportHostError}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </VStack>
          </>
        }
        footer={
          <HStack gap={3} justifyContent="flex-end">
            <Button
              type="button"
              variant="outline"
              colorPalette="neutral"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {text('devices.common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              colorPalette="brand"
              onClick={() => runSubmit(false)}
              loading={isSubmitting}
              disabled={!hasChanges || hasTransportHostError}
            >
              {text('devices.massEdit.saveButton', 'Save {{count}} device', {
                count: selectedDevices.length,
              })}
            </Button>
            <Button
              type="button"
              colorPalette="brand"
              variant="outline"
              onClick={() => runSubmit(true)}
              loading={isSubmitting}
              disabled={!hasChanges || hasTransportHostError}
            >
              {text('devices.common.saveAndReboot', 'Save & Reboot')}
            </Button>
          </HStack>
        }
      />
    )
  },
)

DeviceMassEditDialog.displayName = 'DeviceMassEditDialog'
